/**
 * Reactive view of the offline write queue.
 *
 * A queue with no UI is indistinguishable from data loss — the user types a
 * comment, it disappears from the composer, and nothing tells them whether it
 * reached the server. Everything here exists to answer "what is still waiting,
 * and what went wrong".
 */
import type { OfflineLedgerEntry } from '@/lib/offline/offline-queue'
import { useCallback, useMemo, useState, useSyncExternalStore } from 'react'
import { useRequestScope } from '@/hooks/useRequestScope'
import { flushOfflineLedger, offlineLedger } from '@/lib/offline/ledger'
import { createLedgerView } from '@/lib/offline/ledger-view'
import {
  selectBlockedWrites,
  selectOwnerWrites,
  selectPendingWrites,
} from '@/lib/offline/offline-queue'
import {
  discardOfflineWrite,
  retryOfflineWrite,
} from '@/lib/offline/queue-actions'
import { createOfflineRecoveryActions } from '@/lib/offline/recovery'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { onlineManager, useQueryClient } from '@tanstack/react-query'

const ledgerView = createLedgerView(offlineLedger)

export interface PendingWritesState {
  /** Everything not yet confirmed by the server, oldest first. */
  pending: OfflineLedgerEntry[]
  /** The subset that will not move without the user: failed, stale, ambiguous. */
  blocked: OfflineLedgerEntry[]
  count: number
  blockedCount: number
  retry: (entry: OfflineLedgerEntry) => Promise<void>
  discard: (entry: OfflineLedgerEntry) => Promise<void>
  organizationId: string | null
  actionError?: string
}

export function usePendingWrites(): PendingWritesState {
  const queryClient = useQueryClient()
  const ownerId = useAuth(state => state.user?.id ?? null)
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id ?? null,
  )
  const actionScope = useRequestScope()
  const allEntries = useSyncExternalStore(
    ledgerView.subscribe,
    ledgerView.read,
    ledgerView.read,
  )
  const [feedback, setFeedback] = useState<{
    scope: typeof actionScope
    message?: string
  }>()
  const actions = useMemo(
    () =>
      createOfflineRecoveryActions({
        session: actionScope,
        ownerId,
        organizationId,
        ledger: offlineLedger,
        isOnline: () => onlineManager.isOnline(),
        retry: entry => retryOfflineWrite(queryClient, offlineLedger, entry),
        discard: entry =>
          discardOfflineWrite(queryClient, offlineLedger, entry),
        flush: flushOfflineLedger,
      }),
    [actionScope, ownerId, organizationId, queryClient],
  )
  const entries = selectOwnerWrites(allEntries, ownerId)

  const pending = selectPendingWrites(entries)
  const blocked = selectBlockedWrites(entries)

  const runAction = useCallback(
    async (action: 'retry' | 'discard', entry: OfflineLedgerEntry) => {
      const outcome = await actions[action](entry)
      if (outcome.status === 'cancelled') return
      try {
        actionScope.assertCurrent()
      } catch {
        return
      }
      setFeedback({ scope: actionScope, message: outcome.message })
    },
    [actions, actionScope],
  )
  const retry = useCallback(
    (entry: OfflineLedgerEntry) => runAction('retry', entry),
    [runAction],
  )
  const discard = useCallback(
    (entry: OfflineLedgerEntry) => runAction('discard', entry),
    [runAction],
  )

  return {
    pending,
    blocked,
    count: pending.length,
    blockedCount: blocked.length,
    retry,
    discard,
    organizationId,
    actionError: feedback?.scope === actionScope ? feedback.message : undefined,
  }
}
